import "bootstrap/dist/css/bootstrap.min.css";
import { SidebarItem } from "../models/SidebarItem";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import useSidebar from "src/hooks/useSidebar";

interface SidebarProps {
  items: SidebarItem[];
}

const Sidebar = ({ items }: SidebarProps) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const segments = currentPath.split('/').filter(segment => segment !== "");
  const lastSegment = segments[segments.length - 1];
  const { isOpen, setIsOpen } = useSidebar();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const toggleDropdown = (label: string) => {
    setOpenDropdown(openDropdown === label ? null : label);
  };

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const isChildActive = (child: SidebarItem) => lastSegment === child.lastSegment;

  return (
    <div className={`sidebar ${isOpen ? "open" : ""}`}>
      <div className="sidebar-header">
        <i
          className={isOpen ? "bi bi-x h3" : "bi bi-list h3"}
          onClick={toggleSidebar}
        ></i>
      </div>
      <div className="menu">
        <ul className="">
          {items.map((item) => {
            const hasChildren = !!item.subItems?.length;
            const isExpanded = openDropdown === item.name;
            const isDirectActive =
              !hasChildren && lastSegment === (item.lastSegment ?? item.path);

            return (
              <li
                key={item.name}
                className={`${hasChildren ? "has-children" : ""}${
                  isDirectActive ? " active" : ""
                }`.trim()}
              >
                {hasChildren ? (
                  // Collapsed rail: the category button is disabled so the icon
                  // isn't clickable; hovering reveals the flyout instead.
                  <button
                    className="dropdown-btn"
                    onClick={() => {
                      if (isOpen) toggleDropdown(item.name);
                    }}
                    disabled={!isOpen}
                    aria-expanded={isExpanded}
                  >
                    {item.icon && (
                      <i className={`${item.icon} menu-icon`} aria-hidden="true"></i>
                    )}
                    <span>{item.name}</span>
                    <span className="icon">{isExpanded ? "▲" : "▼"}</span>
                  </button>
                ) : (
                  <Link
                    to={item?.path ?? ""}
                    className="menu-link"
                    onClick={(e) => {
                      // Collapsed rail icons don't navigate; the tooltip link does.
                      if (!isOpen) e.preventDefault();
                    }}
                  >
                    {item.icon && (
                      <i className={`${item.icon} menu-icon`} aria-hidden="true"></i>
                    )}
                    <span>{item.name}</span>
                  </Link>
                )}

                {/* Inline accordion submenu (open mode). */}
                {hasChildren && (
                  <ul className={`sub-menu ${isExpanded ? "show" : ""}`}>
                    <div>
                      {item.subItems?.map((child) => (
                        <li
                          key={child.name}
                          className={isChildActive(child) ? "active" : ""}
                        >
                          <Link to={child?.path ?? ""}>
                            <span>{child.name}</span>
                          </Link>
                        </li>
                      ))}
                    </div>
                  </ul>
                )}

                {/* Collapsed-mode flyout: revealed on hover when the rail is collapsed. */}
                {hasChildren && (
                  <div className="sidebar-flyout">
                    <div className="sidebar-flyout-title">{item.name}</div>
                    <ul>
                      {item.subItems?.map((child) => (
                        <li
                          key={child.name}
                          className={isChildActive(child) ? "active" : ""}
                        >
                          <Link to={child?.path ?? ""}>{child.name}</Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Collapsed-mode tooltip for a direct link (e.g. Dashboard). */}
                {!hasChildren && item.path && (
                  <div className="sidebar-tooltip">
                    <Link to={item.path}>{item.name}</Link>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default Sidebar;
